import { getClient } from "app/services/missionStorageApi";

export interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  subjectTemplate: string;
  bodyTemplate: string;
  bodyFormat: "markdown" | "html" | "text";
  createdAt: string;
  updatedAt: string;
}

export interface LogEmailRule {
  id: string;
  name: string;
  enabled: boolean;
  templateId: string;
  criteria: Record<string, unknown>;
  additionalRecipients: { to: string[]; cc: string[]; bcc: string[] };
  maxAttempts: number;
  priority: number;
  extractClip: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmailNotificationSettings {
  defaultRecipients: { to: string[]; cc: string[]; bcc: string[] };
}

export interface CreateEmailTemplateInput {
  name: string;
  description?: string;
  enabled?: boolean;
  subjectTemplate: string;
  bodyTemplate: string;
  bodyFormat?: "markdown" | "html" | "text";
}

export interface UpdateEmailTemplateInput {
  name?: string;
  description?: string;
  enabled?: boolean;
  subjectTemplate?: string;
  bodyTemplate?: string;
  bodyFormat?: "markdown" | "html" | "text";
}

export interface CreateLogEmailRuleInput {
  name: string;
  enabled?: boolean;
  templateId: string;
  criteria: Record<string, unknown>;
  additionalRecipients: { to: string[]; cc: string[]; bcc: string[] };
  maxAttempts?: number;
  priority?: number;
  extractClip?: boolean;
}

export interface UpdateLogEmailRuleInput {
  name?: string;
  enabled?: boolean;
  templateId?: string;
  criteria?: Record<string, unknown>;
  additionalRecipients?: { to: string[]; cc: string[]; bcc: string[] };
  maxAttempts?: number;
  priority?: number;
  extractClip?: boolean;
}

const TEMPLATE_FIELDS = `
  id
  name
  description
  enabled
  subjectTemplate
  bodyTemplate
  bodyFormat
  createdAt
  updatedAt
`;

const RULE_FIELDS = `
  id
  name
  enabled
  templateId
  criteria
  additionalRecipients
  maxAttempts
  priority
  extractClip
  createdAt
  updatedAt
`;

export async function fetchEmailTemplates(
  arcHost?: string | null,
  arcId?: string | null,
): Promise<EmailTemplate[]> {
  const data = await getClient(arcHost, arcId).request<{ emailTemplates: EmailTemplate[] }>(`
    query {
      emailTemplates {
        ${TEMPLATE_FIELDS}
      }
    }
  `);
  return data.emailTemplates;
}

export async function createEmailTemplate(
  input: CreateEmailTemplateInput,
  arcHost?: string | null,
  arcId?: string | null,
): Promise<EmailTemplate> {
  const data = await getClient(arcHost, arcId).request<{ createEmailTemplate: EmailTemplate }>(
    `mutation CreateEmailTemplate($input: CreateEmailTemplateInput!) {
      createEmailTemplate(input: $input) {
        ${TEMPLATE_FIELDS}
      }
    }`,
    { input }
  );
  return data.createEmailTemplate;
}

export async function updateEmailTemplate(
  id: string,
  input: UpdateEmailTemplateInput,
  arcHost?: string | null,
  arcId?: string | null,
): Promise<EmailTemplate> {
  const data = await getClient(arcHost, arcId).request<{ updateEmailTemplate: EmailTemplate }>(
    `mutation UpdateEmailTemplate($id: String!, $input: UpdateEmailTemplateInput!) {
      updateEmailTemplate(id: $id, input: $input) {
        ${TEMPLATE_FIELDS}
      }
    }`,
    { id, input }
  );
  return data.updateEmailTemplate;
}

export async function deleteEmailTemplate(
  id: string,
  arcHost?: string | null,
  arcId?: string | null,
): Promise<boolean> {
  const data = await getClient(arcHost, arcId).request<{ deleteEmailTemplate: boolean }>(
    `mutation DeleteEmailTemplate($id: String!) {
      deleteEmailTemplate(id: $id)
    }`,
    { id }
  );
  return data.deleteEmailTemplate;
}

export async function fetchLogEmailRules(
  arcHost?: string | null,
  arcId?: string | null,
): Promise<LogEmailRule[]> {
  const data = await getClient(arcHost, arcId).request<{ logEmailRules: LogEmailRule[] }>(`
    query {
      logEmailRules {
        ${RULE_FIELDS}
      }
    }
  `);
  return data.logEmailRules;
}

export async function createLogEmailRule(
  input: CreateLogEmailRuleInput,
  arcHost?: string | null,
  arcId?: string | null,
): Promise<LogEmailRule> {
  const data = await getClient(arcHost, arcId).request<{ createLogEmailRule: LogEmailRule }>(
    `mutation CreateLogEmailRule($input: CreateLogEmailRuleInput!) {
      createLogEmailRule(input: $input) {
        ${RULE_FIELDS}
      }
    }`,
    { input }
  );
  return data.createLogEmailRule;
}

export async function updateLogEmailRule(
  id: string,
  input: UpdateLogEmailRuleInput,
  arcHost?: string | null,
  arcId?: string | null,
): Promise<LogEmailRule> {
  const data = await getClient(arcHost, arcId).request<{ updateLogEmailRule: LogEmailRule }>(
    `mutation UpdateLogEmailRule($id: String!, $input: UpdateLogEmailRuleInput!) {
      updateLogEmailRule(id: $id, input: $input) {
        ${RULE_FIELDS}
      }
    }`,
    { id, input }
  );
  return data.updateLogEmailRule;
}

export async function deleteLogEmailRule(
  id: string,
  arcHost?: string | null,
  arcId?: string | null,
): Promise<boolean> {
  const data = await getClient(arcHost, arcId).request<{ deleteLogEmailRule: boolean }>(
    `mutation DeleteLogEmailRule($id: String!) {
      deleteLogEmailRule(id: $id)
    }`,
    { id }
  );
  return data.deleteLogEmailRule;
}

export async function fetchEmailNotificationSettings(
  arcHost?: string | null,
  arcId?: string | null,
): Promise<EmailNotificationSettings> {
  const data = await getClient(arcHost, arcId).request<{ emailNotificationSettings: EmailNotificationSettings }>(`
    query {
      emailNotificationSettings {
        defaultRecipients
      }
    }
  `);
  return data.emailNotificationSettings;
}

export async function updateEmailNotificationSettings(
  defaultRecipients: { to: string[]; cc: string[]; bcc: string[] },
  arcHost?: string | null,
  arcId?: string | null,
): Promise<EmailNotificationSettings> {
  const data = await getClient(arcHost, arcId).request<{
    updateEmailNotificationSettings: EmailNotificationSettings;
  }>(
    `mutation UpdateEmailNotificationSettings($defaultRecipients: JSON!) {
      updateEmailNotificationSettings(defaultRecipients: $defaultRecipients) {
        defaultRecipients
      }
    }`,
    { defaultRecipients }
  );
  return data.updateEmailNotificationSettings;
}
